import { useState, useEffect } from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";

// Components
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import SocialProof from "@/components/SocialProof";
import FeaturedIn from "@/components/FeaturedIn";
import SignalsSection from "@/components/SignalsSection";
import MethodSection from "@/components/MethodSection";
import LeadMagnet from "@/components/LeadMagnet";
import Programs from "@/components/Programs";
import CountdownBanner from "@/components/CountdownBanner";
import About from "@/components/About";
import Testimonials from "@/components/Testimonials";
import InstagramReels from "@/components/InstagramReels";
import RetreatsSection from "@/components/RetreatsSection";
import Footer from "@/components/Footer";
import QuizModal from "@/components/QuizModal";
import BookingModal from "@/components/BookingModal";
import PaymentSuccess from "@/components/PaymentSuccess";
import AuthCallback from "@/components/AuthCallback";
import Dashboard from "@/components/Dashboard";
import Members from "@/components/Members";
import ModuleDetail from "@/components/ModuleDetail";
import MoreQuizzes from "@/components/MoreQuizzes";
import DetoxFreebie from "@/components/DetoxFreebie";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const HomePage = ({ onOpenQuiz, onOpenBooking }) => {
  return (
    <>
      <Hero onOpenQuiz={onOpenQuiz} onOpenBooking={onOpenBooking} />
      <SocialProof />
      <FeaturedIn />
      <SignalsSection />
      <MethodSection />
      <LeadMagnet onOpenQuiz={onOpenQuiz} />
      <Programs onOpenBooking={onOpenBooking} />
      <CountdownBanner onOpenBooking={onOpenBooking} />
      <About onOpenBooking={onOpenBooking} />
      <Testimonials />
      <InstagramReels />
      <RetreatsSection onOpenBooking={onOpenBooking} />
    </>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Router that detects OAuth callback fragment BEFORE regular routing
const AppRouter = ({ onOpenQuiz, onOpenBooking }) => {
  const location = useLocation();

  // Detect session_id in URL fragment - process auth callback first
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <HomePage onOpenQuiz={onOpenQuiz} onOpenBooking={onOpenBooking} />
        }
      />
      <Route
        path="/programs"
        element={<Programs standalone onOpenBooking={onOpenBooking} />}
      />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/members" element={<Members />} />
      <Route path="/members/:moduleId" element={<ModuleDetail />} />
      <Route path="/quizzes" element={<MoreQuizzes onOpenResetQuiz={onOpenQuiz} />} />
      <Route path="/decode-guide" element={<DetoxFreebie />} />
      <Route path="/free-detox" element={<Navigate to="/decode-guide" replace />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
    </Routes>
  );
};

function App() {
  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  // Cross-route trigger: any component can dispatch `open-quiz-modal`
  useEffect(() => {
    const handler = () => setIsQuizOpen(true);
    window.addEventListener("open-quiz-modal", handler);
    return () => window.removeEventListener("open-quiz-modal", handler);
  }, []);

  return (
    <div className="App min-h-screen bg-[#F9F8F6]">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Header
            onOpenQuiz={() => setIsQuizOpen(true)}
            onOpenBooking={() => setIsBookingOpen(true)}
          />
          <main>
            <AppRouter
              onOpenQuiz={() => setIsQuizOpen(true)}
              onOpenBooking={() => setIsBookingOpen(true)}
            />
          </main>
          <Footer onOpenBooking={() => setIsBookingOpen(true)} />

          {/* Modals */}
          <QuizModal isOpen={isQuizOpen} onClose={() => setIsQuizOpen(false)} />
          <BookingModal
            isOpen={isBookingOpen}
            onClose={() => setIsBookingOpen(false)}
          />

          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
